import React, { Fragment } from 'react';
import { HorizontalWrapper } from 'moet-core';

import {
  ButtonQuaternary,
  SpacerVertical,
  SpacerHorizontal,
  Link,
  AlertBlock,
} from '@klarna/bubble-ui';
import { ContainerTemplate, CallToActionButton, SecondaryButton } from '@/components';
import { OnboardingField } from '@/bpc/components/OnboardingFieldComponent/OnboardingField';
import { OnboardingFieldSetComponent } from '@/bpc/components/OnboardingFieldComponent/OnboardingFieldSetComponent';
import { OnboardingGroup } from '@/bpc/components/OnboardingGroupComponent/OnboardingGroup';
import {
  WithTermsAndConditions,
  WithTermsAndConditionsChildProps,
} from '@/bpc/components/HOCs/WithTermsAndConditions';
import { Onboarding } from '@/bpc/components/BaseComponents/Onboarding';
import {
  getInfoBoxText,
  makeSubtitleElement,
} from '@/bpc/components/OnboardingGroupComponent/messageRenderer';
import { ANALYTICS_EVENTS } from '@/bpc/utils/analyticsEvents';
import { ErrorAlert } from '@/components/ErrorAlert';
import styled from 'styled-components';
export interface OnboardingGroupComponentProps {
  group: OnboardingGroup;
  error?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  t: TranslatorFunction;
  tArray: ArrayTranslatorFunction<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  tKey: TranslatorFunctionKey;
  hasPreviousGroup: boolean;
  hasNextGroup: boolean;
  onPreviousGroup: () => void;
  onNextGroup: () => void;
  onNextSubgroup: () => void;
  onShowSummaryPage: () => void;
  onSubmit: () => void;
  onboarding: Onboarding;
  isLoading: boolean;
  shouldSkipSummaryPage: boolean;
  entryPoint?: string;
}

interface OnboardingGroupComponentState {
  highlightMandatoryButEmptyFields: boolean;
  isLoading: boolean;
  ctaClicked: boolean;
  errorOnSubmit: string | null;
  errorMessage: string | undefined;
  showErrorOnTop: boolean;
}

type ComponentProps = OnboardingGroupComponentProps & WithTermsAndConditionsChildProps;

class _OnboardingGroupComponent extends React.Component<
  ComponentProps,
  OnboardingGroupComponentState
> {
  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      highlightMandatoryButEmptyFields: false,
      ctaClicked: false,
      isLoading: false,
      errorOnSubmit: null,
      errorMessage: undefined,
      showErrorOnTop: false,
    };

    this.onAddItem = this.onAddItem.bind(this);
    this.onDeleteItem = this.onDeleteItem.bind(this);
    this.onBlurOfField = this.onBlurOfField.bind(this);
    this.handleOnBack = this.handleOnBack.bind(this);
    this.handleOnNext = this.handleOnNext.bind(this);
    this.navigateToEntryPoint = this.navigateToEntryPoint.bind(this);
    this.handleOnSubmit = this.handleOnSubmit.bind(this);
    this.callToActionOnNextWrapper = this.callToActionOnNextWrapper.bind(this);
  }

  private onAddItem(e): void {
    e.preventDefault();
    this.props.group.addItem();
    this.forceUpdate();
  }

  private onBlurOfField(field: OnboardingField) {
    return (value): void => {
      if (typeof value === 'boolean') {
        if (value === null || value === undefined) {
          return;
        }
      } else if (typeof value !== 'boolean') {
        if (!value) {
          return;
        }
      }

      this.props.group.setValueOfField(field, value);
      // to recalculate whether to disable Next button
      this.forceUpdate();
    };
  }

  private onDeleteItem(index: number): void {
    this.props.group.deleteItem(index);
    this.forceUpdate();
  }

  private callToActionOnNextWrapper(group: OnboardingGroup, onNextFn: () => void): void {
    const { missingTermsValidation } = this.props;

    if (!group.allMandatoryFilled() || missingTermsValidation) {
      this.setState({ ctaClicked: true, highlightMandatoryButEmptyFields: true });
      return;
    }

    this.setState({ highlightMandatoryButEmptyFields: false });
    return onNextFn();
  }

  private navigateToEntryPoint(): void {
    const { entryPoint, onboarding } = this.props;
    if (entryPoint) {
      onboarding.logEvent(ANALYTICS_EVENTS.entryPointClicked, { entryPoint });
      window.location.href = entryPoint;
    }
  }

  private handleOnBack(): void {
    const { hasPreviousGroup, onPreviousGroup } = this.props;
    if (
      this.props.group.fields[0][0].absolutePath ===
      'mor_group_answers.is_applicable_for_group_onboarding'
    ) {
      this.setState({
        showErrorOnTop: true,
        errorMessage: undefined,
      });
    }

    if (hasPreviousGroup) {
      onPreviousGroup();
    } else {
      this.navigateToEntryPoint();
    }
  }

  private async handleOnSubmit(): Promise<void> {
    const {
      onSubmit,
      onboarding,
      onSubmitTermsAndConditionsFn,
      missingTermsValidation,
      hasTermsToAgree,
    } = this.props;

    // Early return - missing accept terms OR has already accepted terms
    if (missingTermsValidation) {
      this.setState({ ctaClicked: true, errorOnSubmit: null });
      return;
    }

    this.setState({ isLoading: true, errorOnSubmit: null });

    try {
      const submitTermsPromise = hasTermsToAgree ? onSubmitTermsAndConditionsFn : Promise.resolve;
      await submitTermsPromise();
      await onboarding.pushPayloadToBackendAndTriggerContinueEvent(this.props.group.groupId);
      await onboarding.submitApplication();
      this.setState({ isLoading: false });
      await onSubmit();
    } catch (errorOnSubmit) {
      this.setState({ isLoading: false, errorOnSubmit });
      // Scroll to the top
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }

  preventNavigation(): boolean | undefined {
    const { t, group } = this.props;
    const fieldPath = group.fields[0][0].absolutePath;
    const shouldPreventNavigation =
      fieldPath === 'mor_group_answers.is_applicable_for_group_onboarding' &&
      group.payload.mor_group_answers &&
      group.payload.mor_group_answers?.is_applicable_for_group_onboarding === false;

    if (shouldPreventNavigation) {
      this.setState({
        showErrorOnTop: true,
        errorMessage: t('group.mandatory_confirmation.applies_to_all_entities_error'),
      });
    } else {
      this.setState({
        showErrorOnTop: true,
        errorMessage: undefined,
      });
    }
    return shouldPreventNavigation;
  }

  handleOnNext(): void {
    const {
      group,
      hasNextGroup,
      onNextGroup,
      onShowSummaryPage,
      hasTermsToAgree,
      shouldSkipSummaryPage,
    } = this.props;
    const { callToActionOnNextWrapper } = this;

    if (this.preventNavigation()) {
      return;
    }

    if (hasNextGroup) {
      return callToActionOnNextWrapper(group, onNextGroup);
    }

    if (hasTermsToAgree && shouldSkipSummaryPage) {
      return callToActionOnNextWrapper(group, this.handleOnSubmit);
    }

    return callToActionOnNextWrapper(group, onShowSummaryPage);
  }

  addItemBtn(translationPrefix, isMorOnboarding): React.ReactNode {
    const { t, group } = this.props;
    if (
      isMorOnboarding &&
      group.canAddAdditionalItem() &&
      group.fields[0][0].type === 'array_of_enum'
    ) {
      return;
    }
    if (group.canAddAdditionalItem()) {
      return (
        <>
          <div
            style={{
              flex: 0,
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <ButtonQuaternary size="small" onClick={this.onAddItem}>
              {t(`${translationPrefix}add_aditional_item`)}
            </ButtonQuaternary>
          </div>
          <SpacerVertical spaceToken="space/150" />
        </>
      );
    }
  }

  render(): React.ReactNode {
    const {
      group,
      error,
      hasPreviousGroup,
      t,
      tKey,
      TermsAndConditionsComponent,
      shouldSkipSummaryPage,
      entryPoint,
    } = this.props;

    const translationPrefix = 'products-fe.general.buttons.';
    const nextButtonTranslationKey = `${translationPrefix}${
      shouldSkipSummaryPage ? 'submit_application' : 'continue'
    }`;
    const entityDocumentTranslationPrefix = 'products-fe.components.entity_widget_document';

    const { ctaClicked, isLoading, errorOnSubmit } = this.state;
    const showBackButton = hasPreviousGroup || !!entryPoint;

    const isMorOnboarding =
      group.payload?.contract_onboarding_type === 'MOR_ONBOARDING_GROUP' ||
      group.payload?.contract_onboarding_type === 'MOR_ONBOARDING_ENTITY';

    return (
      <ContainerTemplate
        title={t(group.labelTitle) || group.fallbackTitle}
        subtitle={makeSubtitleElement(group, t) || group.fallbackSubtitle}
        error={error || errorOnSubmit}
      >
        {this.state.showErrorOnTop && this.state.errorMessage ? (
          <StyledWrapper>
            <ErrorAlert error={this.state.errorMessage} />
          </StyledWrapper>
        ) : null}

        {isMorOnboarding && getInfoBoxText(group, t)}
        {isMorOnboarding &&
          group.payload?.contract_onboarding_type === 'MOR_ONBOARDING_ENTITY' &&
          !hasPreviousGroup && (
            <>
              <AlertBlock title={t(`${entityDocumentTranslationPrefix}.info_box_title`)}>
                {t(`${entityDocumentTranslationPrefix}.info_box_content`)}
                <Link
                  href={t(`${entityDocumentTranslationPrefix}.download_text.link`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(`${entityDocumentTranslationPrefix}.download_text.text`)}
                </Link>
              </AlertBlock>
              <SpacerVertical spaceToken="space/200" />
            </>
          )}
        <SpacerVertical spaceToken="space/400" />

        {group.canHaveMultipleItems() ? (
          <>
            {group.fields.map((fields, i) => (
              // TODO don't use index as key prop
              // https://robinpokorny.com/blog/index-as-a-key-is-an-anti-pattern/
              <Fragment key={`item-${i}`}>
                <OnboardingFieldSetComponent
                  fields={fields}
                  getValueForField={group.getValueForField}
                  setValueOfField={group.setValueOfField}
                  onBlurField={this.onBlurOfField}
                  onDelete={group.canDeleteItem() ? (): void => this.onDeleteItem(i) : undefined}
                  t={t}
                  tKey={tKey}
                  highlightMandatoryButEmptyFields={this.state.highlightMandatoryButEmptyFields}
                />
                <SpacerVertical spaceToken="space/100" />
              </Fragment>
            ))}
            {this.addItemBtn(translationPrefix, isMorOnboarding)}
          </>
        ) : (
          <OnboardingFieldSetComponent
            fields={group.fields[0]}
            getValueForField={group.getValueForField}
            setValueOfField={group.setValueOfField}
            onBlurField={this.onBlurOfField}
            highlightMandatoryButEmptyFields={this.state.highlightMandatoryButEmptyFields}
            t={t}
            tKey={tKey}
          />
        )}
        <TermsAndConditionsComponent isLoading={isLoading} ctaClicked={ctaClicked} />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {showBackButton ? (
            <>
              <SecondaryButton onClick={this.handleOnBack}>
                {t(`${translationPrefix}back`)}
              </SecondaryButton>
              <SpacerHorizontal spaceToken="space/300" />
            </>
          ) : null}
          <CallToActionButton onClick={this.handleOnNext} loading={isLoading} disabled={isLoading}>
            {t(nextButtonTranslationKey)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

export const OnboardingGroupComponent = WithTermsAndConditions<OnboardingGroupComponentProps>(
  _OnboardingGroupComponent,
);

const StyledWrapper = styled.div`
  position: fixed;
  bottom: auto;
  z-index: 99;
  left: 50%;
  top: 0;
  transform: translate(-50%, 0);
  border-radius: 1em;
`;
